// Détecter un poste dont l'horloge avance assez pour que `supabase-js` juge
// chaque jeton neuf déjà expiré.
//
// POURQUOI CE MODULE EXISTE
// -------------------------
// auth-js compare `expires_at` (écrit par le serveur) à `Date.now()` (lu sur le
// POSTE), avec une marge de 90 s (GoTrueClient.ts:1649-1650, lib/constants.ts:13).
// Avec des jetons de 3 600 s, un poste en avance de plus de 58,5 minutes voit
// tout jeton neuf déjà expiré : chaque requête relance un rafraîchissement,
// jusqu'au 429, puis le SDK efface la session. Cas réel : Oxyd salon, le
// 2026-09-11, 360 rafraîchissements et 24 reconnexions. Trace :
// `docs/features/auth-inscription/2026-09-13-tempete-jetons-oxyd-horloge-du-poste.md`.
//
// Il n'importe RIEN, comme `sessionExpectation.ts` : `toast.ts` et
// `AuthContext.tsx` l'importent tous deux, sans cycle possible.

/** Recopie de `EXPIRY_MARGIN_MS` d'auth-js (lib/constants.ts:13). */
export const SDK_EXPIRY_MARGIN_MS = 90_000;

/** Durée pendant laquelle les messages d'erreur cèdent la place au message d'horloge. */
export const CLOCK_SKEW_SUPPRESSION_MS = 15_000;

// Instant MONOTONE (`performance.now()`) de la dernière détection.
//
// ⛔ Jamais `Date.now()` ici : l'horloge système est précisément celle qui ment,
// et elle peut sauter pendant la fenêtre si la personne la corrige.
let detecteA: number | null = null;

/**
 * Le test EXACT du SDK, appliqué à une session reçue par SIGNED_IN ou
 * TOKEN_REFRESHED. auth-js ne livre jamais une telle session dans les 90 s de
 * son expiration sans l'avoir rafraîchie : si elle paraît déjà expirée à son
 * arrivée, seule une horloge en avance l'explique.
 *
 * Rend `false` si `expiresAtSec` est absent : dans le doute, on ne bloque
 * personne.
 */
export function isSessionStaleOnArrival(expiresAtSec: number | undefined, nowMs: number): boolean {
  if (typeof expiresAtSec !== 'number' || !Number.isFinite(expiresAtSec)) return false;
  return expiresAtSec * 1000 - nowMs < SDK_EXPIRY_MARGIN_MS;
}

/** Arme ou réarme la fenêtre. `monoNowMs` vient de `performance.now()`. */
export function markClockSkewDetected(monoNowMs: number): void {
  detecteA = monoNowMs;
}

/** Vrai pendant CLOCK_SKEW_SUPPRESSION_MS après la dernière détection. */
export function isClockSkewSuppressing(monoNowMs: number): boolean {
  if (detecteA === null) return false;
  return monoNowMs - detecteA < CLOCK_SKEW_SUPPRESSION_MS;
}

/** Une session saine est arrivée : le poste est à l'heure. */
export function clearClockSkew(): void {
  detecteA = null;
}

/** Remise à zéro entre deux tests. */
export function __resetClockSkewForTests(): void {
  detecteA = null;
}
